import { useEffect } from 'react'

import { useStore } from 'zustand'

import { useVersionService } from '../VersionContext'
import { createVersionStore } from '../VersionStore'

/**
 * Store inerte para quando não há provider acima. Hooks não podem ser
 * condicionais, e `useReloadGuard` é adotado dentro de fluxos (level-play,
 * check-in) que também são montados fora do app inteiro — em teste, por
 * exemplo. Uma guarda de reload não pode ser motivo de crash.
 */
const FALLBACK_STORE = createVersionStore()

/** Estado de versão para leitura — diagnóstico, painel de staff, telas. */
export const useVersion = () =>
  useStore(
    useVersionService()?.store ?? FALLBACK_STORE,
    ({ currentVersion, latestVersion, releaseType, updateAvailable, lastCheck }) => ({
      currentVersion,
      latestVersion,
      releaseType,
      updateAvailable,
      lastCheck
    })
  )

export const useUpdateAvailable = () =>
  useStore(useVersionService()?.store ?? FALLBACK_STORE, s => s.updateAvailable)

/**
 * Registra um motivo para não recarregar agora enquanto `active` for verdadeiro.
 *
 * É a interface inteira da guarda: qualquer fluxo que não possa ser
 * interrompido chama isto com uma razão sua, e `UpdatePolicy.canReload()`
 * passa a devolver `false` sem saber o que essa razão significa. A próxima
 * feature crítica se protege com uma linha, sem editar a política.
 */
export const useReloadGuard = (active: boolean, reason: string) => {
  const service = useVersionService()

  useEffect(() => {
    if (!service || !active) return undefined

    service.store.getState().addBlock(reason)

    // O cleanup é o que impede um bloqueio órfão: se o componente desmonta no
    // meio do fluxo (navegação, erro, troca de rota), a razão sai junto e o
    // reload adiado volta a ser possível.
    return () => service.store.getState().removeBlock(reason)
  }, [service, active, reason])
}
